import {
  useState,
  useEffect,
  SyntheticEvent,
  FormEvent,
  useCallback,
} from "react";
import { Switch } from "@headlessui/react";
import Image from "next/image";
import { useRouter } from "next/router";
import { doc } from "firebase/firestore";
import { updatePassword } from "@firebase/auth";
import Dropzone from "react-dropzone";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { toast } from "react-toastify";
import { useDocument } from "react-firebase-hooks/firestore";
import * as Sentry from "@sentry/browser";
import { NextSeo } from "next-seo";
import { throttle } from "throttle-debounce";
import { useCopyToClipboard } from "react-use";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";

import Navbar from "components/Landing/Navbar";
import { db } from "utils/firebase";
import { Layout } from "components/common";
import { useAuth } from "context/AuthContext";
import { updateUser, checkUsernameExists } from "./api/user";
import { USER_VALIDATIONS } from "constants/validationRules";
import Link from "next/link";
import { Avatar } from "components/common";
import { getInitials, validateURL } from "utils/textUtils";
import { storage } from "utils/firebase";
import { dataURItoBlob } from "utils/imageUtils";
import goToBillingPortal from "utils/stripe/goToBillingPortal";
import usePremiumStatus from "hooks/app/usePremiumStatus";
import { createCheckoutSession } from "utils/stripe/createCheckoutSession";
import {
  MAX_EDIT_ACCOUNT_PER_SECOND,
  SOFT_LIMIT_EDIT_ACCOUNT_PER_SECOND,
} from "constants/hardLimits";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Settings() {
  const { user, logout } = useAuth();
  const router = useRouter();
  const userIsPremium = usePremiumStatus(user);
  const [userProfile] = useDocument(doc(db, `users`, user?.uid));

  const [copyState, copyToClipboard] = useCopyToClipboard();

  // Temp hard limits
  const [firstChange, setFirstChange] = useState(null);
  const [numberOfChanges, setNumberOfChanges] = useState(0);

  const [name, setName] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [bio, setBio] = useState<string>("");
  const [profileImage, setProfileImage] = useState(null);
  const [usernameError, setUsernameError] = useState<string>("");

  const [password, setPassword] = useState<string>("");

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userProfileData = userProfile?.data();

    if (userProfileData?.name) setName(userProfileData.name);
    if (userProfileData?.username) setUsername(userProfileData.username);
    if (userProfileData?.bio) setBio(userProfileData.bio);
    if (userProfileData?.profileImageUrl)
      setProfileImage(userProfileData.profileImageUrl);

    setIsLoading(false);
  }, [userProfile]);

  const handleSave = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Temp hard limits
    const saveTime = new Date().getTime();

    if (!firstChange || saveTime - 1000 >= firstChange) {
      setFirstChange(saveTime);
      setNumberOfChanges(1);
    } else {
      if (numberOfChanges >= MAX_EDIT_ACCOUNT_PER_SECOND) {
        toast.error("`Please try again in 10 seconds.`");
        return false;
      } else {
        setNumberOfChanges((changes) => changes + 1);

        if (numberOfChanges >= SOFT_LIMIT_EDIT_ACCOUNT_PER_SECOND) {
          Sentry.captureMessage(
            `Soft limit account edit reached - ${user.uid}`
          );
        }
      }
    }

    if (isLoading) return;
    setIsLoading(true);

    const validate = (boolResult: boolean, errorMessage: string): boolean => {
      if (!boolResult) {
        setIsLoading(false);
        toast.error(errorMessage);
        return false;
      }

      return true;
    };

    if (
      !validate(
        name.length >= USER_VALIDATIONS.nameMinLength,
        "Please enter a valid name."
      )
    )
      return;
    if (
      !validate(
        name.length <= USER_VALIDATIONS.nameMaxLength,
        `Your username cannot exceed ${USER_VALIDATIONS.nameMaxLength} characters.`
      )
    )
      return;
    if (!validate(!usernameError, `Username already exists`)) return;
    if (
      !validate(
        username.length >= USER_VALIDATIONS.usernameMinLength,
        `Your username needs to be at least ${USER_VALIDATIONS.usernameMinLength} characters.`
      )
    )
      return;
    if (
      !validate(
        username.length <= USER_VALIDATIONS.usernameMaxLength,
        `Your username cannot exceed ${USER_VALIDATIONS.usernameMaxLength} characters.`
      )
    )
      return;

    if (
      !validate(
        bio.length <= USER_VALIDATIONS.bioMaxLength,
        `Your bio cannot exceed ${USER_VALIDATIONS.bioMaxLength} characters.`
      )
    )
      return;

    if (user?.uid) {
      if (
        profileImage &&
        profileImage !== userProfile?.data()?.profileImageUrl
      ) {
        const profileStorageRef = ref(storage, `users/${user.uid}/profile`);

        const profileUploadTask = uploadBytesResumable(
          profileStorageRef,
          dataURItoBlob(profileImage)
        );

        await profileUploadTask.on(
          "state_changed",
          (snapshot) => {},
          (error) => {
            toast.error("There was a problem uploading your profile image.");
            setIsLoading(false);
          },
          () => {
            getDownloadURL(profileUploadTask.snapshot.ref).then(
              (downloadURL) => {
                setProfileImage(downloadURL);

                updateUser({
                  docId: user.uid,
                  details: {
                    profileImageUrl: downloadURL,
                  },
                })
                  .then(() => {})
                  .catch((err) => {
                    toast.error("Something went wrong");
                    console.error(err);
                  });
              }
            );
          }
        );
      } else if (!profileImage) {
        updateUser({
          docId: user.uid,
          details: {
            profileImageUrl: "",
          },
        })
          .then(() => {})
          .catch((err) => {
            toast.error("Something went wrong");
            console.error(err);
          });
      }

      await updateUser({
        docId: user.uid,
        details: {
          name,
          username,
          bio,
        },
      })
        .then(() => {
          toast.success("Saved!");
        })
        .catch((err) => {
          toast.error("Something went wrong");
          console.error(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const hasChanges = () =>
    userProfile?.data()?.name !== name ||
    userProfile?.data()?.username !== username ||
    userProfile?.data()?.bio !== bio ||
    !userProfile?.data()?.profileImageUrl ||
    userProfile?.data()?.profileImageUrl !== profileImage;

  const handleNameChange = (e: FormEvent<HTMLInputElement>) =>
    setName(e.currentTarget.value);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkUsername = useCallback(
    throttle(
      700,
      async (newUsername) => {
        const usernameExists = await checkUsernameExists(newUsername);

        if (usernameExists && userProfile?.data()?.username !== newUsername) {
          setUsernameError("Username already exists");
        } else {
          setUsernameError("");
        }
      },
      { noLeading: true }
    ),
    [userProfile]
  );

  const handleUsernameChange = (e: FormEvent<HTMLInputElement>) => {
    const newUsername = e.currentTarget.value;

    setUsername(newUsername);

    checkUsername(newUsername);
  };

  const handleBioChange = (e: FormEvent<HTMLTextAreaElement>) =>
    setBio(e.currentTarget.value);

  const onProfilePhotoDrop = useCallback((acceptedFiles) => {
    const reader = new FileReader();
    if (!acceptedFiles.length) toast.error("Unsupported image");

    reader.onabort = () => console.log("File reading was aborted");
    reader.onerror = () => console.log("File reading has failed");
    reader.onloadend = () => {
      setProfileImage(reader.result);
    };
    reader.readAsDataURL(acceptedFiles[0]);
  }, []);

  const handleRemoveProfileImage = () => setProfileImage(null);

  const handlePasswordChange = (e: FormEvent<HTMLInputElement>) =>
    setPassword(e.currentTarget.value);

  const handleChangePassword = () => {
    // TODO add modal, to retype password, reauthenticate, and call updatePassword(newPassword)
  };

  const handleLogoutClick = async () => {
    await logout();
    router.push("/login");
  };

  const handleOpenPaymentPage = () => {
    if (userIsPremium) goToBillingPortal();
    else createCheckoutSession(user?.uid);
  };

  return (
    <>
      <NextSeo title="Settings" description="Account settings" />

      <main className="relative min-h-screen w-screen overflow-x-hidden">
        <div className="relative isolate mt-[80px] flex w-screen flex-col items-center justify-center overflow-hidden px-10 py-8">
          <h1 className="text-center text-[5vw] font-bold tracking-tight text-gray-900 sm:text-4xl ">
            Account settings
          </h1>

          <div className="relative flex min-h-full w-full max-w-5xl flex-col justify-center  sm:px-6 lg:px-8">
            <div className="mx-auto mt-8 w-full px-4 ">
              <h1 className="font-small mt-2 py-2  text-xl tracking-tight text-black ">
                Profile
              </h1>
              <div className="text-sm text-gray-400">
                What actionable content do you want to create?
              </div>

              <div className="bg-white py-8">
                <form
                  className="space-y-8"
                  action="#"
                  method="POST"
                  onSubmit={handleSave}
                >
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Name
                    </label>
                    <div className="mt-1">
                      <input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        required
                        className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-emerald-500 sm:text-sm"
                        onChange={handleNameChange}
                        maxLength={USER_VALIDATIONS.nameMaxLength}
                        disabled={isLoading}
                        value={name.substring(
                          0,
                          USER_VALIDATIONS.nameMaxLength
                        )}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="username"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Username
                    </label>
                    <div className="mt-1">
                      <div className="mt-1 flex rounded-md shadow-sm">
                        <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
                          @
                        </span>
                        <input
                          type="text"
                          name="username"
                          id="username"
                          maxLength={USER_VALIDATIONS.usernameMaxLength}
                          value={username.substring(
                            0,
                            USER_VALIDATIONS.usernameMaxLength
                          )}
                          autoComplete="current-username"
                          required
                          onChange={handleUsernameChange}
                          className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 px-3 py-2 focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="mt-1 text-sm text-red-400">
                      {usernameError}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="bio"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Bio
                    </label>
                    <div className="mt-1">
                      <textarea
                        rows={4}
                        name="bio"
                        id="bio"
                        maxLength={USER_VALIDATIONS.bioMaxLength}
                        value={bio.substring(0, USER_VALIDATIONS.bioMaxLength)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm"
                        onChange={handleBioChange}
                      />
                    </div>
                    <div className="mt-1 text-sm text-gray-400">
                      Write a few sentences about yourself.
                    </div>
                  </div>

                  <div>
                    <div className="block text-sm font-medium text-gray-700">
                      Photo
                    </div>
                    <div className="mt-2 flex flex-row content-center">
                      <Avatar
                        initials={getInitials(name)}
                        imageUrl={profileImage}
                        size="l"
                      />
                      {profileImage ? (
                        <div className="flex flex-wrap content-center">
                          <button
                            onClick={handleRemoveProfileImage}
                            className="mx-4 cursor-pointer rounded-md border border-gray-300 px-3 py-1 text-sm font-medium hover:bg-gray-100 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <Dropzone
                          onDrop={onProfilePhotoDrop}
                          accept={{
                            "image/png": [".png", ".gif", ".jpeg", ".jpg"],
                          }}
                          maxFiles={1}
                          maxSize={5000000}
                          noClick
                        >
                          {({ getRootProps, getInputProps, open }) => (
                            <div
                              className="mx-4 flex w-full flex-wrap content-center"
                              {...getRootProps()}
                            >
                              <label
                                className="flex h-full w-full flex-wrap content-center text-sm font-medium text-gray-700"
                                htmlFor="profilePhoto"
                              >
                                <input
                                  {...getInputProps()}
                                  onClick={open}
                                  accept="image/*"
                                  type="file"
                                  id="profilePhoto"
                                  className="flex w-full cursor-pointer flex-wrap content-center justify-center text-sm text-gray-900 file:mr-4 file:cursor-pointer file:rounded-md file:border file:border-solid file:border-gray-300 file:bg-white file:px-3 file:py-1 focus:outline-none  file:focus:border-emerald-500 file:focus:outline-none  file:focus:ring-emerald-500 dark:text-gray-400 dark:placeholder-gray-400 "
                                />
                                <span className="cursor-pointer rounded-md border border-solid border-gray-300 bg-white px-3 py-1 text-gray-700 hover:bg-gray-100 focus:border-emerald-500 focus:outline-none focus:ring-emerald-500">
                                  Upload
                                </span>
                              </label>
                            </div>
                          )}
                        </Dropzone>
                      )}
                    </div>
                  </div>

                  <div>
                    <div>
                      <h3 className="text-lg font-medium">
                        Manage Subscription
                      </h3>
                      <div className="text-sm text-gray-400">
                        View and manage the status of your Act Now Button
                        subscription.
                      </div>
                    </div>

                    <div className="pt-6">
                      <button
                        disabled={isLoading}
                        type="button"
                        onClick={handleOpenPaymentPage}
                        className=" flex justify-center rounded-md border border-transparent bg-emerald-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-emerald-600 disabled:opacity-70"
                      >
                        Open Payment page
                      </button>
                    </div>
                  </div>

                  <div className="pt-6">
                    <button
                      disabled={!hasChanges() || isLoading}
                      type="submit"
                      className=" flex w-full justify-center rounded-md border border-transparent bg-emerald-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-emerald-600 disabled:opacity-70"
                    >
                      Save
                    </button>
                  </div>
                </form>

                <button
                  onClick={handleLogoutClick}
                  className="mt-5 flex w-full items-center justify-center rounded-md border border-transparent bg-gray-100 px-3 py-2 text-sm font-medium leading-4 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                >
                  Logout
                </button>
              </div>
            </div>

            <div className="mt-3 w-full sm:mx-auto">
              <div className="py-8 px-4 text-sm font-normal text-gray-400 shadow sm:rounded-lg sm:px-10">
                Check out Act Now Button&apos;s{" "}
                <Link href="/privacy" className="underline" target={"_blank"}>
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link
                  href="/terms-and-conditions"
                  className="underline"
                  target={"_blank"}
                >
                  Terms & Conditions
                </Link>{" "}
                here.
              </div>
            </div>
          </div>
        </div>
        <Navbar />
      </main>
    </>
  );
}

export default Settings;
